import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { ArrowLeft, LogOut, Wrench, Ticket, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

function fmtDate(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("en-US"); } catch { return iso; } }

export default function AdminCoupons() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [coupons, setCoupons] = useState(null);
  const [openNew, setOpenNew] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/coupons");
      setCoupons(data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not load coupons");
    }
  };
  useEffect(() => { load(); }, []);

  const handleLogout = () => { logout(); nav("/login"); };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/coupons/${c.id}`, { active: !c.active });
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Update failed");
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/coupons/${c.id}`);
      toast.success(`Deleted ${c.code}`);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-10 bg-slate-900 text-white px-4 sm:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center"><Wrench className="w-4 h-4" /></div>
          <div className="font-bold" style={{ fontFamily: "Outfit" }}>PlumberHQ · Admin</div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-400 hidden sm:inline">{user?.email}</span>
          <ChangePasswordDialog />
          <Button onClick={handleLogout} variant="outline" className="h-9 bg-transparent text-white border-slate-700 hover:bg-slate-800 rounded-lg" data-testid="admin-logout"><LogOut className="w-4 h-4 mr-1" /> Log out</Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5">
        <Link to="/admin" className="inline-flex items-center text-sm text-slate-600 font-semibold"><ArrowLeft className="w-4 h-4 mr-1" /> Back to admin</Link>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Coupons</h1>
            <p className="text-slate-500 text-sm mt-1">Create promo codes plumbers can apply at checkout. Discounts are validated on the backend.</p>
          </div>
          <NewCouponDialog open={openNew} onOpenChange={setOpenNew} onCreated={load} />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {coupons === null ? (
            <div className="p-6 text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : coupons.length === 0 ? (
            <div className="p-8 text-center">
              <Ticket className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500 text-sm">No coupons yet — create your first one.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-3">Code</th>
                    <th className="text-left py-3 px-3">Discount</th>
                    <th className="text-left py-3 px-3">Expires</th>
                    <th className="text-right py-3 px-3">Uses</th>
                    <th className="text-center py-3 px-3">Active</th>
                    <th className="text-right py-3 px-3"> </th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((c) => {
                    const capped = c.max_uses != null && c.uses >= c.max_uses;
                    return (
                      <tr key={c.id} className="border-b border-slate-100" data-testid={`coupon-row-${c.code}`}>
                        <td className="py-3 px-3 font-mono font-bold tracking-wide">{c.code}</td>
                        <td className="py-3 px-3">
                          {c.discount_type === "percent" ? `${c.discount_value}% off` : `$${c.discount_value} off`}
                        </td>
                        <td className="py-3 px-3 text-slate-600">{fmtDate(c.expires_at)}</td>
                        <td className="py-3 px-3 text-right">
                          <span className={capped ? "text-red-600 font-semibold" : ""}>{c.uses}</span>
                          {c.max_uses != null && <span className="text-slate-400"> / {c.max_uses}</span>}
                          {capped && <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-red-100 text-red-700" data-testid={`coupon-max-${c.code}`}>Max</span>}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <Switch checked={c.active} onCheckedChange={() => toggleActive(c)} data-testid={`coupon-toggle-${c.code}`} />
                        </td>
                        <td className="py-3 px-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => del(c)} className="text-red-600 hover:bg-red-50 h-8" data-testid={`coupon-delete-${c.code}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NewCouponDialog({ open, onOpenChange, onCreated }) {
  const [code, setCode] = useState("");
  const [discountType, setDiscountType] = useState("fixed");
  const [discountValue, setDiscountValue] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCode(""); setDiscountType("fixed"); setDiscountValue(""); setExpiresAt(""); setMaxUses("");
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      code: code.trim().toUpperCase(),
      discount_type: discountType,
      discount_value: Number(discountValue),
      expires_at: expiresAt ? new Date(expiresAt + "T23:59:59Z").toISOString() : null,
      max_uses: maxUses ? Number(maxUses) : null,
      active: true,
    };
    if (!payload.code) { toast.error("Code required"); return; }
    if (!(payload.discount_value > 0)) { toast.error("Discount must be > 0"); return; }
    if (payload.discount_type === "percent" && payload.discount_value > 100) { toast.error("Percent cannot exceed 100"); return; }
    setSaving(true);
    try {
      await api.post("/admin/coupons", payload);
      toast.success(`Created coupon ${payload.code}`);
      reset();
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not create coupon");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl h-11 px-4" data-testid="new-coupon-btn">
          <Plus className="w-4 h-4 mr-1" /> New coupon
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="new-coupon-dialog">
        <DialogHeader>
          <DialogTitle>New coupon</DialogTitle>
          <DialogDescription>Plumbers can apply this code on Settings → Billing.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Code</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="TESTPAY1" className="h-11 font-mono uppercase" required data-testid="coupon-code" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <select value={discountType} onChange={(e) => setDiscountType(e.target.value)} className="h-11 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="coupon-type">
                <option value="fixed">Fixed $ off</option>
                <option value="percent">Percent off</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Value {discountType === "percent" ? "(%)" : "($)"}</Label>
              <Input type="number" step="0.01" min="0.01" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} placeholder={discountType === "percent" ? "20" : "48"} className="h-11" required data-testid="coupon-value" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Max uses (optional)</Label>
              <Input type="number" min="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="Unlimited" className="h-11" data-testid="coupon-max-uses" />
            </div>
            <div>
              <Label className="text-xs">Expires (optional)</Label>
              <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="h-11" data-testid="coupon-expires" />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 font-semibold rounded-xl" data-testid="coupon-submit">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating…</> : "Create coupon"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
